
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Users, Coins, Repeat, MapPin } from "lucide-react";
import { format } from "date-fns";
import { BirdieGameGroup, Course } from "@/types";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";

interface GameInfoCardProps {
  game: BirdieGameGroup;
  course?: Course;
  actions?: React.ReactNode;
}

const GameInfoCard = ({ game, course, actions }: GameInfoCardProps) => {
  if (!game) return null;

  const getDateDisplay = () => {
    if (!game.startDate) return String(game.year);
    const start = new Date(game.startDate);
    if (game.durationType === 'yearly') {
      return `Jan 1 – Dec 31, ${game.year}`;
    }
    if (game.durationType === 'custom' && game.endDate) {
      const end = new Date(game.endDate);
      const startYear = start.getUTCFullYear();
      const endYear = end.getUTCFullYear();
      const startStr = format(start, startYear === endYear ? "MMM d" : "MMM d, yyyy");
      const endStr = format(end, "MMM d, yyyy");
      return `${startStr} – ${endStr}`;
    }
    // open_ended
    return `Started ${format(start, "MMM d, yyyy")}`;
  };

  return (
    <Card className="shadow-sm border-t-2 border-t-forest rounded-xl mb-6 animate-fade-in">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          {/* Left: details */}
          <div className="space-y-2 min-w-0">
            {course && (
              <div className="flex items-center text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 mr-1.5 shrink-0" />
                {course.name}, {course.location}
              </div>
            )}

            <div className="flex items-center text-sm text-muted-foreground">
              <Calendar className="h-4 w-4 mr-1.5 shrink-0" />
              {getDateDisplay()}
            </div>

            <div className="flex items-center text-sm text-muted-foreground">
              <Users className="h-4 w-4 mr-1.5 shrink-0" />
              <div className="flex -space-x-2 ml-0.5">
                {game.members.map((player) => (
                  <div key={player.id} className="relative">
                    <AvatarPlaceholder
                      user={player}
                      size="sm"
                      className="border-2 border-white"
                    />
                  </div>
                ))}
              </div>
            </div>

            {game.bet && (
              <div className="flex items-center text-sm text-muted-foreground">
                <Coins className="h-4 w-4 mr-1.5 shrink-0" />
                {game.bet}
              </div>
            )}
          </div>

          {/* Right: actions + status badges */}
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            {actions}
            {game.isSample && (
              <Badge variant="outline" className="text-muted-foreground border-muted-foreground/30">
                Sample
              </Badge>
            )}
            {game.isRecurring && (
              <Badge variant="outline" className="text-forest border-forest">
                <Repeat className="h-3 w-3 mr-1" />
                Recurring
              </Badge>
            )}
            {game.isArchived && (
              <Badge variant="outline" className="bg-muted">
                Completed
              </Badge>
            )}
            {game.isPaid && (
              <Badge className="bg-forest/10 text-forest hover:bg-forest/20">
                Paid
              </Badge>
            )}
            {!game.isArchived && (
              <Badge className="bg-forest/10 text-forest hover:bg-forest/20">
                Active
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default GameInfoCard;
